import { z } from 'zod';

/**
 * Contrato das paginas publicas do site (growup.gg): arenas e contactos.
 *
 *   - `GET  /api/arenas`   arenas publicadas no back-office (Arena > Configuracao);
 *   - `POST /api/contact`  formulario de contacto.
 */

// ---------------------------------------------------------------------------
// Arenas
// ---------------------------------------------------------------------------

/** 1 = segunda ... 7 = domingo. Um dia sem entradas esta fechado. */
export const openingSlotSchema = z.object({
  weekday: z.number().int().min(1).max(7),
  opens: z.string(),
  closes: z.string(),
});
export type OpeningSlot = z.infer<typeof openingSlotSchema>;

/**
 * Mesma forma que `publishedArenaSchema` do arena-shared: a site-api le-a da
 * rota interna da arena-api e passa-a ao site tal como vem.
 */
export const publicArenaSchema = z.object({
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  address: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  mapUrl: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  openingHours: z.array(openingSlotSchema),
  hoursNote: z.string().nullable(),
});
export type PublicArena = z.infer<typeof publicArenaSchema>;

// ---------------------------------------------------------------------------
// Contactos
// ---------------------------------------------------------------------------

export const contactSubjectSchema = z.enum([
  'geral',
  'socios',
  'arena',
  'loja',
  'parcerias',
  'direcao',
]);
export type ContactSubject = z.infer<typeof contactSubjectSchema>;

export const CONTACT_SUBJECT_LABELS: Record<ContactSubject, string> = {
  geral: 'Pergunta geral',
  socios: 'Sócios e associados',
  arena: 'Arena',
  loja: 'Loja e encomendas',
  parcerias: 'Parcerias e patrocínios',
  direcao: 'Direção',
};

/**
 * "Direção" (F-018) so aparece a quem tem sessao e e associado com quota em
 * dia, membro atual de uma equipa ou streamer ativo; vai por
 * `POST /api/contact/direcao`, com a conta de quem escreve.
 */
export const DIRECTION_SUBJECT = 'direcao' satisfies ContactSubject;

/** Os assuntos abertos a todos (`POST /api/contact`). */
export const PUBLIC_CONTACT_SUBJECTS = contactSubjectSchema.options.filter(
  (s) => s !== DIRECTION_SUBJECT,
);

/** Porque esta conta pode escrever a Direcao. */
export const directionReasonSchema = z.enum(['associate', 'team', 'streamer']);
export type DirectionReason = z.infer<typeof directionReasonSchema>;

export const DIRECTION_REASON_LABELS: Record<DirectionReason, string> = {
  associate: 'Associado com quota em dia',
  team: 'Membro de uma equipa',
  streamer: 'Streamer',
};

/** `GET /api/me/direcao`: vazio = o assunto Direção nao aparece. */
export const myDirectionAccessSchema = z.object({ reasons: z.array(directionReasonSchema) });
export type MyDirectionAccess = z.infer<typeof myDirectionAccessSchema>;

export const contactRequestSchema = z.object({
  name: z.string().trim().min(2, 'Diz-nos o teu nome.').max(120),
  email: z.string().trim().toLowerCase().email('Email inválido.').max(200),
  subject: contactSubjectSchema,
  message: z
    .string()
    .trim()
    .min(10, 'A mensagem é muito curta.')
    .max(4000, 'A mensagem é muito longa (máximo 4000 caracteres).'),
  consent: z.literal(true, {
    errorMap: () => ({ message: 'Tens de aceitar a política de privacidade.' }),
  }),
  /** Campo escondido: so um robo o preenche. */
  website: z.string().max(200).optional(),
});
export type ContactRequest = z.infer<typeof contactRequestSchema>;
